import React, { useState, useEffect } from 'react';
import './App.css';
import DeviceSelector from './components/DeviceSelector';
import ResultsDisplay from './components/ResultsDisplay';
import { calculateMass, fetchDevices } from './services/api';

function App() {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([
    { deviceType: '', quantity: 1 }
  ]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const deviceList = await fetchDevices();
      setDevices(deviceList);
      // Set first selector to first device if available
      if (deviceList.length > 0 && selectedDevices[0].deviceType === '') {
        setSelectedDevices([{ deviceType: deviceList[0], quantity: 1 }]);
      }
    } catch (err) {
      setError('Failed to load device list');
      console.error(err);
    }
  };

  const handleAddSelector = () => {
    if (selectedDevices.length < 10) {
      setSelectedDevices([
        ...selectedDevices,
        { deviceType: devices[0] || '', quantity: 1 }
      ]);
    }
  };

  const handleRemoveSelector = (index) => {
    if (selectedDevices.length > 1) {
      const newDevices = [...selectedDevices];
      newDevices.splice(index, 1);
      setSelectedDevices(newDevices);
    }
  };

  const handleDeviceChange = (index, deviceType) => {
    const newDevices = [...selectedDevices];
    newDevices[index].deviceType = deviceType;
    setSelectedDevices(newDevices);
  };

  const handleQuantityChange = (index, quantity) => {
    const newDevices = [...selectedDevices];
    newDevices[index].quantity = quantity;
    setSelectedDevices(newDevices);
  };

  const handleCalculate = async () => {
    // Validate inputs
    const invalidDevices = selectedDevices.filter(
      device => !device.deviceType || device.quantity <= 0
    );
    
    if (invalidDevices.length > 0) {
      setError('Please select device types and enter valid quantities (greater than 0)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const calculationResults = await calculateMass(selectedDevices);
      setResults(calculationResults);
    } catch (err) {
      setError(err.message || 'Calculation failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedDevices([{ deviceType: devices[0] || '', quantity: 1 }]);
    setResults(null);
    setError('');
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Калькулятор массы упаковки</h1>
        <p className="subtitle">Расчет массы упаковки для устройств</p>
      </header>

      <main className="App-main">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}

        <div className="calculator-container">
          <div className="selectors-section">
            <div className="section-header">
              <h2>Выберите устройства и количество</h2>
              <button 
                onClick={handleAddSelector} 
                className="add-button"
                disabled={selectedDevices.length >= 10}
              >
                + Добавить устройство
              </button>
            </div>

            <div className="selectors-list">
              {selectedDevices.map((device, index) => (
                <DeviceSelector
                  key={index}
                  index={index}
                  deviceType={device.deviceType}
                  quantity={device.quantity}
                  devices={devices}
                  onDeviceChange={handleDeviceChange}
                  onQuantityChange={handleQuantityChange}
                  onRemove={handleRemoveSelector}
                  canRemove={selectedDevices.length > 1}
                />
              ))}
            </div>

            <div className="actions">
              <button 
                onClick={handleCalculate} 
                className="calculate-button"
                disabled={loading}
              >
                {loading ? 'Вычисление...' : 'Рассчитать'}
              </button>
              <button onClick={handleReset} className="reset-button">
                Сбросить
              </button>
            </div>
          </div>

          <div className="results-section">
            <h2>Результаты расчета</h2>
            <ResultsDisplay results={results} loading={loading} />
          </div>
        </div>

        <div className="info-section">
          <h3>О калькуляторе</h3>
          <p>
            Этот калькулятор определяет общую массу упакованных устройств, включая массу устройств и массу коробок.
            Он рассчитывает количество необходимых коробок на основе того, сколько устройств помещается в каждую коробку.
          </p>
          <p>
            <strong>Примечание:</strong> Данные могут периодически обновляться. Backend API предоставляет актуальную информацию из таблицы данных.
          </p>
        </div>
      </main>

      <footer className="App-footer">
        <p>Калькулятор массы упаковки &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

export default App;